/* global bassConfig */
/**
 * @file
 * Basstrakk Leaderboard: Final Day?
 *
 * A single component that can render the Basstrakk Leaderboard.
 */

import * as React from 'react';

// Components
import ApiTable from '../../components/ApiTable/ApiTable';
import BassComponent from '../../components/BassComponent/BassComponent';
import NormalTable from './components/NormalTable';

// Config
import meta from './meta';

// Types
import {
  AnglerId,
  IBasstrakkAngler,
  ICutline,
  TournamentId,
} from '../../types';
import { ISponsors } from './types';

// Util
import * as api from '../../util/api';
import { refreshRate, sponsorRows } from '../../util/util';

// Constants
const CATCHES_FETCH_RATE = refreshRate([15000, 15000]);
const LEADERBOARD_FETCH_RATE = refreshRate([30000, 30000]);

/**
 * BASSTrakk Leaderboard
 *
 * Aggregates 2 sources of data
 * 1. basstrakk-leaderboard - Handled via ApiTable, table of ranked anglers
 * 2. basstrakk-catches - Handled via BasstrakkLeaderboard, used in expando
 *
 * Tracks selected row, shows sub-table results.
 */
interface IProps {
  tid: TournamentId;
  // Optional sponsor list for the given tournament.
  sponsors?: ISponsors;
  timezone: string;
  cutlines: ICutline[];
  co: string;
}
interface IState {
  active: AnglerId;
  rows: IBasstrakkAngler[];
}

export class BasstrakkLeaderboardOpen extends React.PureComponent<
  IProps,
  IState
> {
  public static defaultProps = {
    cutlines: [],
  };
  public state: IState = {
    active: 0, // Dummy start AnglerID
    rows: [],
  };

  // Overall leaderboard table data
  private leaderboardApi = '/data/basstrakk-leaderboard';
  private newLeaderboardApi = '/tournament/basstrakk/leaderboard?tournament_id=';

  /**
   * Set active row. Set to 0 if same row clicked.
   */
  public setActive = (active: number) =>
    // Pass function to setState since referencing state directly
    this.setState(state => ({
      active: state.active === active ? 0 : active,
    }));

  /**
   * Set state data when api returns.
   * NOTE: *returns a function to capture scope*
   */
  public fetchRows = () => (rows: IBasstrakkAngler[]) =>
    // tslint:disable:object-literal-sort-keys
    this.setState({
      rows: sponsorRows(rows, this.props.sponsors),
    });

  public async componentDidMount() {
    window.bassBlocks.setApiBase('/wp-json/data/v1');
    const {
      bass_leaderboard: {
        daydate = '',
      } = {},
    } = bassConfig;
    const uri = `${this.newLeaderboardApi}${this.props.tid}&daydate=${daydate}&CompetitorClass=${this.props.co ? '0' : '1'}`;

    await api.subscribe({
      callback: this.fetchRows(),
      rate: CATCHES_FETCH_RATE,
      uri
    });
  }

  public render() {
    const { tid, cutlines, co, sponsors } = this.props;
    const { active, rows } = this.state;
    const suffix = co ? '_co' : '';

    window.bassBlocks.setApiBase('/wp-json/data/v1');
    const {
      bass_leaderboard: {
        daydate = '',
      } = {},
    } = bassConfig;
    const tableApi = `${this.newLeaderboardApi}${tid}&daydate=${daydate}&CompetitorClass=${co ? '0' : '1'}`;

    return (
      <div
        className="basstrakk-leaderboard basstrakk-opens"
        data-testid="basstrakk-leaderboard"
      >
        <h3 className="basstrakk-leaderboard__title">BASSTrakk Leaderboard</h3>
        {/* Disable meta while testing */}
        <BassComponent meta={meta} showMeta={false}>
          {/* 3. Only responsible for fetching + updating initial leaderboard */}
          <ApiTable
            apiUri={tableApi}
            refreshRate={LEADERBOARD_FETCH_RATE}
          >
            {/* 2. Uses custom table feature of ApiTable to render nested */}
            <NormalTable
              active={active}
              cutlines={cutlines}
              setActive={this.setActive}
              sponsors={sponsors}
              rows={rows}
            />
          </ApiTable>
        </BassComponent>
      </div>
    );
  }
}

export default BasstrakkLeaderboardOpen;
